import { CauseId } from "#shared/errors"
import { X_ERROR_SOURCE_ID, X_ERROR_TRACE_ID } from "#shared/headers";
import { errors, type ApiError } from "~/utils/errors"  

function formatDetails(obj: unknown, indent = 0): string {
    const spaces = '  '.repeat(indent);
  
    if (obj !== null && obj !== undefined) {
        if (typeof obj === 'string') return `"${obj}"`;
        if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);
        
        if (Array.isArray(obj)) {
        return obj.map(item => 
            `${spaces}- ${typeof item === 'object' ? '\n' + formatDetails(item, indent + 1) : formatDetails(item, 0)}`
        ).join('\n');
        }
        
        return Object.entries(obj)
        .map(([key, value]) => 
            `${spaces}${key}: ${typeof value === 'object' && value !== null ? '\n' + formatDetails(value, indent + 1) : formatDetails(value, 0)}`
        ).join('\n');
    } else {
        return ''
    }
}
  
/**
 * Custom application error class to hold status code and cause ID.
 * @param causeId The cause ID that occurred.
 * @param details Technical details about the error like variable contents.
 * @param options Error options.
 * @param apiError The original error response from the server API.
 * @param fallbackUrl Optional URL to redirect to after showing alert dialog.
 */
export class ClientError extends Error {
    problem?: string
    solution?: string
    details?: Record<string, unknown>
    causeId?: CauseId
    sourceId?: number
    traceId?: string
    apiError?: ApiError
    fallbackUrl?: string

    constructor(
        causeId: CauseId,
        details?: Record<string, unknown>, 
        options?: ErrorOptions,
        fallbackUrl?: string,
        apiError?: ApiError
    ) {
        const { problem, solution } = errors.translateCauseId(causeId)
        const headers = apiError?.response?.headers
        let sourceId: number | undefined
        let traceId: string | undefined
        if (headers) {
            const sourceIdHeader = headers?.get(X_ERROR_SOURCE_ID)
            sourceId = sourceIdHeader ? Number(sourceIdHeader) : undefined
            traceId = headers?.get(X_ERROR_TRACE_ID) || undefined
        }

        const solutionText = solution ? `\nSolution: ${solution}` : ''
        const causeIdText = causeId ? `\nCause ID: ${causeId.toString(16)}` : ''
        const sourceIdText = sourceId ? `\nSource ID: ${sourceId}` : ''
        const traceText = traceId ? `\nTrace ID: ${traceId}` : ''
        const detailsText = details ? `\nDetails: ${formatDetails(details)}` : '';
        const message = `\nProblem: ${problem}${solutionText}${causeIdText}${sourceIdText}${traceText}\nTime: ${new Date()}${detailsText}`
        
        super(message, options)
        this.problem = problem;
        this.solution = solution;
        this.details = details;
        this.causeId = causeId;
        this.sourceId = sourceId;
        this.traceId = traceId;
        this.fallbackUrl = fallbackUrl;
        this.apiError = apiError;
    }

    public static unexpected(details?: Record<string, unknown>, options?: ErrorOptions, fallbackUrl?: string) {
        return new ClientError(CauseId.UNEXPECTED_ERROR, details, options, fallbackUrl)
    }

}
  
export default defineNuxtPlugin((nuxtApp) => {
    const toast = useToaster();
    const alertDialog = useAlertDialog();

    const reportToast = (
        problem?: string,
        solution?: string,
        causeId?: number,
        sourceId?: number,
        traceId?: string,
    ) => {
        const problemText = problem ? `<h3>${problem}</h3>` : ''
        const solutionText = solution ? `<p>${solution}</p>` : ''
        const errorCode = causeId ? `<small><p>Error Code: ${causeId.toString(16)}-${sourceId || ''}-${traceId || ''}</p></small>` : ''
        toast.error(`${problemText}${solutionText}${errorCode}`)
    }

    const reportAlert = async (
        problem?: string,
        solution?: string,
        causeId?: number,
        sourceId?: number,
        traceId?: string,
        fallbackUrl?: string
    ) => {
        const problemText = problem || 'An error occurred'
        const solutionText = solution ? `<p>${solution}</p>` : ''
        const errorCode = causeId ? `<small><p>Error Code: ${causeId.toString(16)}-${sourceId || ''}-${traceId || ''}</p></small>` : ''
        const message = `${solutionText}${errorCode}`
        
        await alertDialog.showAlert(problemText, message, () => {
            if (fallbackUrl) {
                navigateTo(fallbackUrl)
            }
        })
    }

    // This works in both SSR and CSR
    // Note there is still another nuxtApp.vueApp.config.errorHandler that logs these error to the console - no need to duplicate
    nuxtApp.hook('vue:error', async (error) => {
        if (error instanceof ClientError) {
            if (error.fallbackUrl) {
                reportAlert(error.problem, error.solution, error.causeId, error.sourceId, error.traceId, error.fallbackUrl)
            } else {
                reportToast(error.problem, error.solution, error.causeId, error.sourceId, error.traceId)
            }
        }
    })
})
